import type { LabelKey, PageKey } from '@/lib/crm-pages';
import { XLogo } from '@/components/x-ads/XLogoIcon';
import { Smartphone } from 'lucide-react';
import { Briefcase, Radar, Rss, ScanSearch, SquareKanban, UserRound } from 'lucide-react';
import { Armchair, BookOpen, Building2, CalendarCheck, Contact, FileKey, FolderKanban, LifeBuoy, Package, ReceiptText, Repeat, Users2 } from 'lucide-react';
import { Images, Scale, LayoutDashboard, Users, Folder, Settings, FileText, Send, Link, Megaphone, Target, Camera, Layers, Rocket, Sparkles, Inbox, Bot, BarChart2, BarChart3, FormInput, Phone, Globe, MessageSquare, FileSignature, Calendar, CalendarClock, BookMarked, Music2, Trophy, ListChecks, Shield, Zap, ClipboardList, Wand2, LucideIcon, Boxes } from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** True for routes that aren't built yet — sidebar shows a 'Soon' badge and clicks toast instead of navigating. */
  comingSoon?: boolean;
  adminOnly?: boolean;
  /** Blueprint page switch — hidden when the company's blueprint (or admin) turns it off. */
  page?: PageKey;
  /** Shows the company's word for this thing instead of `label` (e.g. Contacts → Guests). */
  labelKey?: LabelKey;
}
export interface NavGroup {
  group: string;
  /**
   * Key in MODULE_REGISTRY used for per-company gating (super-admin →
   * Company → Modules). Groups WITHOUT a key (Account, the dynamic Data
   * group handles itself) are always visible.
   */
  moduleKey?: string;
  /** Group icon shown in the sidebar header row — gives the accordion visual anchors. */
  icon?: LucideIcon;
  /** Group header shows the company's word (e.g. Leads → Inquiries). */
  labelKey?: LabelKey;
  /** Live-tested but not signed off — the sidebar shows an "In testing" badge on the group header. */
  inTesting?: boolean;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  { group: 'Overview', icon: LayoutDashboard, moduleKey: 'overview', items: [
    { label: 'Dashboard',  to: '/dashboard', icon: LayoutDashboard, page: 'dashboard' },
    { label: 'Reports',    to: '/reports',   icon: BarChart2, page: 'reports' },
    { label: 'Workflows',  to: '/workflows', icon: Zap, page: 'workflows' },
  ]},
  { group: 'Leads', icon: Inbox, moduleKey: 'leads', labelKey: 'leads', items: [
    { label: 'Inbox',         to: '/leads',              icon: Inbox, page: 'leads' },
    { label: 'Analytics',     to: '/leads/analytics',    icon: BarChart2, page: 'leads_analytics' },
    { label: 'AI Analytics',  to: '/leads/ai-analytics', icon: Sparkles, page: 'leads_ai_analytics' },
    { label: 'AI Review',      to: '/leads/ai-review',   icon: Bot, page: 'leads_ai_review', adminOnly: true },
    { label: 'Bot Flows',     to: '/leads/bot-flows',    icon: Bot },
    { label: 'Web Forms',     to: '/leads/web-forms',    icon: FormInput, page: 'web_forms' },
  ]},
  { group: 'Audience', icon: Users, moduleKey: 'audience', items: [
    { label: 'Contacts', to: '/contacts', icon: Users, page: 'contacts', labelKey: 'contacts' },
    { label: 'Groups',   to: '/groups',   icon: Folder, page: 'groups' },
  ]},
  { group: 'Sales', icon: Target, moduleKey: 'sales', items: [
    { label: 'Deals',     to: '/deals',     icon: Trophy, page: 'deals', labelKey: 'deals' },
    { label: 'Tasks',     to: '/tasks',     icon: ListChecks, page: 'tasks' },
    { label: 'Documents', to: '/documents', icon: FileSignature, page: 'documents' },
    { label: 'Calendar',  to: '/calendar',  icon: Calendar, page: 'calendar' },
  ]},
  { group: 'Appointments', icon: Calendar, moduleKey: 'appointments', labelKey: 'appointments', items: [
    { label: 'Calendar', to: '/appointments',          icon: CalendarClock, page: 'appointments' },
    { label: 'Services', to: '/appointments/services', icon: ClipboardList, page: 'appointment_services', labelKey: 'services' },
    { label: 'Bookings', to: '/appointments/bookings', icon: BookMarked, page: 'bookings', labelKey: 'bookings' },
  ]},
  // Industry pages. Every item is off for the General CRM; the company's
  // blueprint (Food / Technology / Real Estate / Travel) turns on the ones that fit.
  { group: 'Operations', icon: Boxes, moduleKey: 'operations', labelKey: 'operations', items: [
    { label: 'Catalog', to: '/catalog', icon: BookOpen, page: 'catalog', labelKey: 'catalog' },
    { label: 'Orders', to: '/orders', icon: ReceiptText, page: 'orders', labelKey: 'orders' },
    { label: 'Tables', to: '/tables', icon: Armchair, page: 'reservations', labelKey: 'reservations' },
    { label: 'Staff', to: '/staff', icon: Users2, page: 'staff', labelKey: 'staff' },
    { label: 'Stock', to: '/stock', icon: Package, page: 'stock', labelKey: 'stock' },
    { label: 'Tickets', to: '/tickets', icon: LifeBuoy, page: 'tickets', labelKey: 'tickets' },
    { label: 'Projects & time', to: '/projects', icon: FolderKanban, page: 'projects', labelKey: 'projects' },
    { label: 'Retainers', to: '/retainers', icon: Repeat, page: 'retainers', labelKey: 'retainers' },
    { label: 'Properties', to: '/properties', icon: Building2, page: 'properties', labelKey: 'properties' },
    { label: 'Viewings', to: '/viewings', icon: CalendarCheck, page: 'viewings', labelKey: 'viewings' },
    { label: 'Leases', to: '/leases', icon: FileKey, page: 'leases', labelKey: 'leases' },
    { label: 'Owners & tenants', to: '/owners', icon: Contact, page: 'owners' },
  ]},
  { group: 'WhatsApp', icon: MessageSquare, moduleKey: 'whatsapp', items: [
    { label: 'Overview', to: '/whatsapp', icon: BarChart3 },
    { label: 'Automation', to: '/leads/bot-flows', icon: Bot },
    { label: 'Catalog & tools', to: '/whatsapp/catalog', icon: Boxes },
    { label: 'Status ads', to: '/whatsapp/ads', icon: Megaphone },
    { label: 'Chats',         to: '/chats',     icon: MessageSquare },
    { label: 'Templates',     to: '/templates', icon: FileText },
    { label: 'Campaigns',     to: '/campaigns', icon: Send },
    { label: 'Bulk',          to: '/whatsapp-account/bulk', icon: Zap },
    { label: 'Tools',         to: '/whatsapp-account/tools', icon: Shield },
    { label: 'Accounts',      to: '/whatsapp-accounts', icon: Phone },
  ]},
  // AI content generation studio — sits next to the marketing modules it feeds.
  { group: 'AI Studio', icon: Sparkles, moduleKey: 'ai_studio', items: [
    { label: 'AI Studio', to: '/ai-studio', icon: Wand2 },
    { label: 'Media Library', to: '/media-library', icon: Images },
  ]},
  // One AI brain on the website chat and in customers' apps (chat API).
  { group: 'AI Agents', icon: Bot, moduleKey: 'ai_agents', items: [
    { label: 'Agents',        to: '/ai-agents',               icon: Bot },
    { label: 'Conversations', to: '/ai-agents/conversations', icon: MessageSquare },
    { label: 'Knowledge',     to: '/ai-agents/knowledge',     icon: BookOpen },
  ]},
  // Cross-platform ROI rollup — sits above the per-platform ad groups.
  { group: 'Ads', icon: Megaphone, moduleKey: 'ads', items: [
    { label: 'Ads Overview', to: '/ads-overview', icon: BarChart3 },
    { label: 'Automated Rules', to: '/ad-rules', icon: Zap },
    { label: 'A/B Compare', to: '/ads-compare', icon: Scale },
  ]},
  { group: 'Marketing', icon: Smartphone, moduleKey: 'ads', items: [
    { label: 'Apps', to: '/marketing/apps', icon: Smartphone },
  ]},
  // Live matrix + UI E2E passed 2026-09-30; the Call/WhatsApp add-on check is still open (owner decision: label and move on).
  { group: 'Facebook Ads', icon: Megaphone, moduleKey: 'facebook_ads', inTesting: true, items: [
    { label: 'AI Campaign',    to: '/facebook/ai-campaign',  icon: Sparkles },
    { label: 'Build Campaign', to: '/facebook/workspace',    icon: Rocket },
    { label: 'Campaigns',      to: '/facebook/campaigns',    icon: Megaphone },
    { label: 'Creatives',      to: '/facebook/creatives',    icon: Layers },
    { label: 'Audiences',      to: '/facebook/audiences',    icon: Target },
    { label: 'Lead Forms',     to: '/facebook/lead-forms',   icon: ClipboardList },
    { label: 'Page Posts',     to: '/facebook/page',         icon: FileText },
    { label: 'Ad Accounts',    to: '/facebook/accounts',     icon: Link },
  ]},
  // Instagram is a first-class top-level module — matches how SMB owners
  // (especially in MENA F&B/fashion/salon segments) actually think about
  // their marketing: "I run Instagram ads", not "I run Meta ads with IG
  // placement". Under the hood the data is shared (Meta ad-account, FB
  // page as IG actor, same Marketing API) — the separation is UI only.
  // Cross-platform (FB+IG) campaigns appear in BOTH modules with a badge.
  { group: 'Instagram', icon: Camera, moduleKey: 'instagram', items: [
    { label: 'Overview',       to: '/instagram',             icon: Camera },
    { label: 'Posts',          to: '/instagram/posts',       icon: FileText },
    // The IG workspace is the full Meta-Marketing-API builder shell,
    // locked to Instagram-only placements + forced IG actor. Same as
    // /facebook/workspace but with platform="instagram".
    { label: 'Build Campaign', to: '/instagram/workspace',   icon: Rocket },
    { label: 'Campaigns',      to: '/instagram/campaigns',   icon: Megaphone },
  ]},
  { group: 'TikTok Ads', icon: Music2, moduleKey: 'tiktok_ads', items: [
    { label: 'Overview',   to: '/tiktok',            icon: Music2 },
    { label: 'Workspace',  to: '/tiktok/workspace',  icon: Rocket },
    { label: 'Account',    to: '/tiktok/account',    icon: Music2 },
    { label: 'Campaigns',  to: '/tiktok/campaigns',  icon: Megaphone },
    { label: 'Measurement', to: '/tiktok/measurement', icon: BarChart2 },
  ]},
  { group: 'X Ads', icon: XLogo, moduleKey: 'x_ads', items: [
    { label: 'Campaigns',    to: '/x-ads/campaigns',     icon: Megaphone },
    { label: 'New campaign', to: '/x-ads/campaigns/new', icon: Rocket },
    { label: 'Creatives',    to: '/x-ads/creatives',     icon: Layers },
    { label: 'Tools',        to: '/x-ads/tools',         icon: ListChecks },
    { label: 'Reporting',    to: '/x-ads/reporting',     icon: BarChart2 },
    { label: 'Account',      to: '/x-ads/account',       icon: Link },
  ]},
  { group: 'Google Ads', icon: Globe, moduleKey: 'google_ads', items: [
    { label: 'Account',   to: '/google-ads/account',   icon: Globe },
    // Campaigns / ad groups / ads are tabs on one page; a new campaign starts
    // from the objective picker, which opens the workspace for that objective
    // (YouTube and App Promotion included — they aren't separate sidebar items).
    { label: 'Campaigns', to: '/google-ads/campaigns',  icon: Megaphone },
    { label: 'Measurement', to: '/google-ads/tools',    icon: BarChart2 },
  ]},
  // Operator-only: the platform owner's own apps (PrepMDCAT, PrepFCPS…) linked
  // to this company's WhatsApp numbers. Sidebar shows it only when
  // settings/modules returns isPlatformOwner — never for a client tenant.
  // Job Hunter (AI agent) — assign-only: super-admin → Company → Modules → "Job Hunter".
  { group: 'Job Hunter', icon: Briefcase, moduleKey: 'job_hunter', items: [
    { label: 'Overview',      to: '/job-hunter',           icon: Radar },
    { label: 'Matches',       to: '/job-hunter/matches',   icon: Target },
    { label: 'Pipeline',      to: '/job-hunter/pipeline',  icon: SquareKanban },
    { label: 'Agents',        to: '/job-hunter/agents',    icon: Bot },
    { label: 'Candidates',    to: '/job-hunter/candidates', icon: UserRound },
    { label: 'Jobs explorer', to: '/job-hunter/jobs',      icon: ScanSearch },
    { label: 'Employers',     to: '/job-hunter/employers', icon: Building2 },
    { label: 'Sources',       to: '/job-hunter/sources',   icon: Rss },
  ]},
  { group: 'Products', icon: Boxes, moduleKey: 'products', items: [
    { label: 'My Products', to: '/products', icon: Boxes },
  ]},
  { group: 'Account', icon: Settings,   items: [{ label: 'Settings', to: '/settings', icon: Settings }] },
];
